import { type RefObject, useEffect, useState } from 'react';
import { Badge, Icon, TabGroup, TabItem } from '@saviynt/design-system';
import { useIntl } from 'react-intl';

import { useManagePageState } from '../../contexts/ManagePageContext';
import { useEffectAfterMount } from '../../hooks/useEffectAfterMount';
import { type AgentRow } from '../ManagePageTable';

interface SidePanelTabsProps {
  sidepanelRef: RefObject<HTMLElement | null>;
  activeTab: string;
  handleChange: (value: string) => void;
  agent: AgentRow;
}

/**
 * Tab navigation for the agent side panel.
 *
 * Renders a {@link TabGroup} with three tabs:
 * - Overview (tab "1") — general agent info, Dashboard icon.
 * - Labels (tab "2") — agent labels, Tag icon, badge shows `Object.keys(agent.labels).length`.
 * - Owners (tab "3") — ownership info, User icon, badge shows `agent.owners.length`.
 *
 * @param props.sidepanelRef - Ref to the side panel element, used by TabGroup for "more" menu positioning.
 * @param props.activeTab - Currently active tab value (`"1"` | `"2"` | `"3"`).
 * @param props.handleChange - Called with the new tab value on selection change.
 * @param props.agent - Agent data used to derive dynamic badge counts.
 *
 * @returns A three-tab navigation group with icons and count badges.
 */
export default function SidePanelTabs({
  sidepanelRef,
  activeTab,
  handleChange,
  agent,
}: SidePanelTabsProps) {
  // Hooks
  const intl = useIntl();
  const { refreshAgent } = useManagePageState();

  // States
  const [countMap, setCountMap] = useState({ labelCount: 0, ownerCount: 0 });

  // Effects
  const hasIngestionOwner = Boolean(
    agent.agentMetadata?.ownerInfo?.ownerFullName &&
    agent.agentMetadata?.ownerInfo?.ownerEmail
  );

  useEffect(() => {
    setCountMap({
      labelCount: Object.keys(agent.labels).length,
      ownerCount: agent.owners.length + (hasIngestionOwner ? 1 : 0),
    });
  }, [agent, hasIngestionOwner]);

  useEffectAfterMount(() => {
    if (refreshAgent !== null) {
      const targetAgent = refreshAgent[agent.agentId];

      setCountMap((pc) => ({
        labelCount: targetAgent.labels
          ? Object.keys(targetAgent.labels).length
          : pc.labelCount,
        ownerCount: targetAgent.owners
          ? (targetAgent.owners?.length ?? 0) + (hasIngestionOwner ? 1 : 0)
          : pc.ownerCount,
      }));

      return;
    }
  }, [refreshAgent]);

  return (
    <TabGroup
      moreContainerRef={sidepanelRef}
      value={activeTab}
      onChange={handleChange}>
      <TabItem
        label={intl.formatMessage({
          id: '6+BhsD',
          defaultMessage: 'Overview',
          description: 'Overview',
        })}
        value='1'
        icon={<Icon kind='Dashboard' size='medium' />}
      />
      <TabItem
        label={intl.formatMessage({
          id: 'AVvpDs',
          defaultMessage: 'Labels',
          description: 'Labels',
        })}
        value='2'
        icon={<Icon kind='Tag' size='medium' />}
        BadgeComp={
          <Badge
            badgeStyle='outlined'
            color='secondary'
            kind='number'
            size='medium'
            number={countMap.labelCount}
          />
        }
      />
      <TabItem
        label={intl.formatMessage({
          id: '92iPqv',
          defaultMessage: 'Owners',
          description: 'Owners',
        })}
        value='3'
        icon={<Icon kind='User' size='medium' />}
        BadgeComp={
          <Badge
            badgeStyle='outlined'
            color='secondary'
            kind='number'
            size='medium'
            number={countMap.ownerCount}
          />
        }
      />
    </TabGroup>
  );
}
